import { useQuery } from "@apollo/client";
import {
  ArchivedCaseSummaryFinanceDetailsAdhocTransaction,
  CasesCredentialVerificationCaseSummaryContainerArchivedCaseSummaryActivityLogFieldsFragment,
  CasesCredentialVerificationCaseSummaryContainerArchivedCredentialVerificationCaseSummaryCaseDetailsApplicationReviewPrivateCaseDetailsFieldsFragment,
  CasesCredentialVerificationCaseSummaryContainerArchivedCredentialVerificationCaseSummaryCaseDetailsFieldsFragment,
  CasesCredentialVerificationCaseSummaryContainerArchivedCredentialVerificationCaseSummaryDocument,
  CasesCredentialVerificationCaseSummaryContainerFileInfoFieldsFragment,
  CasesCredentialVerificationSummaryContainerArchivedCaseSummaryFinanceDetailsAdhocTransactionFieldsFragment,
  FileInfo
} from "../../../../../../generated";
import { ComponentQueryLoader } from "../../../../../shared/component-query-loader";
import { lookupCountryQuery } from './credential-verification-archive-country-query';
import { Country } from "../../../../../../types/country";
import { TransactionData } from "../../../../../shared/transactions/transactions-lite";
import { Empty } from "antd";
import { Summary } from "./summary";

interface SummaryContainerProps {
  data: {
    archivedCaseId: string;
  };
}

export const SummaryContainer: React.FC<SummaryContainerProps> = (
  props
) => {
  const { data, loading, error } = useQuery(
    CasesCredentialVerificationCaseSummaryContainerArchivedCredentialVerificationCaseSummaryDocument,
    {
      variables: {
        archivedCredentialVerificationCaseSummaryId: props.data.archivedCaseId,
      },
    }
  );

  const {
    data: countryData,
    loading: countryLoading,
    error: countryError,
  } = useQuery(lookupCountryQuery);


  let additionalFiles: FileInfo[] = [];
  if (data?.archivedCredentialVerificationCaseSummary?.files && data?.archivedCredentialVerificationCaseSummary?.files.length > 0) {
    const files = data?.archivedCredentialVerificationCaseSummary?.files;
    additionalFiles = files.filter(file => file !== null && file.name.includes("/additional-private-files/")) as FileInfo[];
  }
  
  let totalSentAmount = 0;
  let totalReceivedAmount = 0;
  if (
    data?.archivedCredentialVerificationCaseSummary?.financeDetails
      ?.transactions
  ) {
    const finance =
      data?.archivedCredentialVerificationCaseSummary?.financeDetails
        ?.transactions ?? null;
    if (finance) {
      if (finance?.adhocTransactions?.length) {
        finance?.adhocTransactions?.
        filter(transaction => {
          const approval = transaction?.approval;
          const transactionRef = transaction?.transactionReference;
          return !((approval?.isApplicantApprovalRequired === true && approval?.isApplicantApproved !== true) || (transactionRef?.isSuccess === false))
        }).forEach((transaction) => {
          const amount = transaction?.amount;
          if (amount !== undefined && amount !== null) {
            if (amount > 0) {
              totalSentAmount += amount;
            } else if (amount < 0) {
              totalReceivedAmount += Math.abs(amount); // Add to total received if negative
            }
          }
        });
      }
      if (finance?.submission) {
        const submissionAmount = finance?.submission?.amount ?? 0;
        totalSentAmount += submissionAmount;
      }
    }
  }

  const submissionTransaction: ArchivedCaseSummaryFinanceDetailsAdhocTransaction =
    {
      amount:
        data?.archivedCredentialVerificationCaseSummary?.financeDetails
          ?.transactions?.submission?.amount ?? undefined,
      reason: `Intealth Requested for ID Verification`,
      transactionReference: {
        referenceId:
          data?.archivedCredentialVerificationCaseSummary?.financeDetails
            ?.transactions?.submission?.transactionReference?.referenceId,
        completedAt:
          data?.archivedCredentialVerificationCaseSummary?.financeDetails
            ?.transactions?.submission?.transactionReference?.completedAt,
      },
    };

    let adhocTransactions: CasesCredentialVerificationSummaryContainerArchivedCaseSummaryFinanceDetailsAdhocTransactionFieldsFragment[] = [];

    if (Array.isArray(data?.archivedCredentialVerificationCaseSummary?.financeDetails?.transactions?.adhocTransactions)) {
      adhocTransactions = data?.archivedCredentialVerificationCaseSummary?.financeDetails.transactions.adhocTransactions.filter(
        (transaction): transaction is CasesCredentialVerificationSummaryContainerArchivedCaseSummaryFinanceDetailsAdhocTransactionFieldsFragment =>{
          const approval = transaction?.approval;
          const transactionRef = transaction?.transactionReference;
          return (
            !!transactionRef &&
            (!approval || approval.isApplicantApprovalRequired !== true || approval.isApplicantApproved === true) &&
            transactionRef.isSuccess !== false
          );
        }
      );
    }

  const allTransactions = [...adhocTransactions, submissionTransaction];
  const transactionData: TransactionData = {
    totalReceived: totalReceivedAmount,
    totalSent: totalSentAmount,
    transactionsData: allTransactions,
  };

  return (
    <ComponentQueryLoader
      loading={loading || countryLoading}
      error={error || countryError}
      hasData={
        !!data?.archivedCredentialVerificationCaseSummary &&
        Object.keys(data.archivedCredentialVerificationCaseSummary).length >
          0 &&
        !!countryData
      }
      hasDataComponent={
        <Summary
          caseDetailsData={
            data?.archivedCredentialVerificationCaseSummary
              ?.caseDetails as CasesCredentialVerificationCaseSummaryContainerArchivedCredentialVerificationCaseSummaryCaseDetailsFieldsFragment
          }
          fileDetails={
            data?.archivedCredentialVerificationCaseSummary
              ?.files as CasesCredentialVerificationCaseSummaryContainerFileInfoFieldsFragment[]
          }
          countryData={
            countryData?.countries?.eventPayload?.countries as Country[]
          }
          transactionData={transactionData}
          privateCaseDetails={
            data?.archivedCredentialVerificationCaseSummary?.caseDetails?.applicationReview?.privateCaseDetails as CasesCredentialVerificationCaseSummaryContainerArchivedCredentialVerificationCaseSummaryCaseDetailsApplicationReviewPrivateCaseDetailsFieldsFragment
          }
          activityData={
            data?.archivedCredentialVerificationCaseSummary
              ?.activityLog as CasesCredentialVerificationCaseSummaryContainerArchivedCaseSummaryActivityLogFieldsFragment[]
          }
          additionalFiles={additionalFiles}
        />
      }
      noDataComponent={
        <Empty
          description={`No archived summary available for case ID: ${props.data.archivedCaseId}`}
        />
      }
    />
  );
};
